import React, { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import toast from 'react-hot-toast';

const currencyOptions = [
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
];

const SchoolFeePlanModal = ({ visible, onCancel, plan }) => {
  const { goals, addSchoolFeePlan, updateSchoolFeePlan } = useData();
  const isEditing = !!plan;

  const [formData, setFormData] = useState({
    childName: '',
    linkedGoalId: '',
    currency: 'GBP',
    startYear: 2029,
    numberOfYears: 7,
    baseFee: 27000,
    annualIncrease: 5,
  });
  const [feeSchedule, setFeeSchedule] = useState([]);
  const [useManualFees, setUseManualFees] = useState(false);

  useEscapeKey(onCancel, visible);

  useEffect(() => {
    if (visible) {
      if (plan) {
        setFormData({
          childName: plan.childName || '',
          linkedGoalId: plan.linkedGoalId || '',
          currency: plan.currency || 'GBP',
          startYear: plan.feeSchedule?.[0]?.startYear || 2029,
          numberOfYears: plan.feeSchedule?.length || 7,
          baseFee: plan.feeSchedule?.[0]?.annualFee || 27000,
          annualIncrease: 5,
        });
        setFeeSchedule(plan.feeSchedule || []);
        setUseManualFees(true);
      } else {
        const defaults = {
          childName: '',
          linkedGoalId: '',
          currency: 'GBP',
          startYear: 2029,
          numberOfYears: 7,
          baseFee: 27000,
          annualIncrease: 5,
        };
        setFormData(defaults);
        setUseManualFees(false);
        // Generate initial fee schedule directly (can't rely on the auto-generate
        // effect since its deps may already match these default values)
        const schedule = [];
        for (let i = 0; i < defaults.numberOfYears; i++) {
          const startYear = defaults.startYear + i;
          schedule.push({
            academicYear: `Year ${7 + i}`,
            startYear,
            endYear: startYear + 1,
            annualFee: Math.round(defaults.baseFee * Math.pow(1 + defaults.annualIncrease / 100, i)),
          });
        }
        setFeeSchedule(schedule);
      }
    }
  }, [visible, plan]);

  // Auto-generate fee schedule when params change
  useEffect(() => {
    if (!useManualFees && formData.baseFee > 0 && formData.numberOfYears > 0) {
      const schedule = [];
      for (let i = 0; i < formData.numberOfYears; i++) {
        const startYear = formData.startYear + i;
        const yearGroup = `Year ${7 + i}`;
        const annualFee = Math.round(formData.baseFee * Math.pow(1 + formData.annualIncrease / 100, i));
        schedule.push({
          academicYear: yearGroup,
          startYear,
          endYear: startYear + 1,
          annualFee,
        });
      }
      setFeeSchedule(schedule);
    }
  }, [formData.startYear, formData.numberOfYears, formData.baseFee, formData.annualIncrease, useManualFees]);

  const handleFeeChange = (index, value) => {
    setUseManualFees(true);
    setFeeSchedule(prev =>
      prev.map((item, i) => i === index ? { ...item, annualFee: parseFloat(value) || 0 } : item)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.childName.trim()) {
      toast.error('Please enter a child name');
      return;
    }
    if (feeSchedule.length === 0) {
      toast.error('Please configure the fee schedule');
      return;
    }

    const data = {
      childName: formData.childName.trim(),
      linkedGoalId: formData.linkedGoalId || null,
      currency: formData.currency,
      feeSchedule,
    };

    if (isEditing) {
      updateSchoolFeePlan(plan.id, data);
      toast.success('School fee plan updated');
    } else {
      addSchoolFeePlan(data);
    }
    onCancel();
  };

  const totalFees = feeSchedule.reduce((sum, f) => sum + f.annualFee, 0);

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {isEditing ? 'Edit School Fee Plan' : 'Add School Fee Plan'}
                  </Dialog.Title>
                  <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Child Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Child Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.childName}
                      onChange={(e) => setFormData({ ...formData, childName: e.target.value })}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. Alice"
                      required
                    />
                  </div>

                  {/* Linked Goal */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Linked Education Goal
                    </label>
                    <select
                      value={formData.linkedGoalId}
                      onChange={(e) => setFormData({ ...formData, linkedGoalId: e.target.value })}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No linked goal</option>
                      {goals.map(goal => (
                        <option key={goal.id} value={goal.id}>{goal.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Link to a savings goal to see projected savings vs fees</p>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {currencyOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Fee Generation Parameters */}
                  <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">Fee Schedule</h4>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Start Year (Sept)</label>
                        <input
                          type="number"
                          value={formData.startYear}
                          onChange={(e) => {
                            setFormData({ ...formData, startYear: parseInt(e.target.value) || 2029 });
                            setUseManualFees(false);
                          }}
                          className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={2024}
                          max={2050}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Number of Years</label>
                        <input
                          type="number"
                          value={formData.numberOfYears}
                          onChange={(e) => {
                            setFormData({ ...formData, numberOfYears: parseInt(e.target.value) || 1 });
                            setUseManualFees(false);
                          }}
                          className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={1}
                          max={15}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Year 1 Fee</label>
                        <input
                          type="number"
                          value={formData.baseFee}
                          onChange={(e) => {
                            setFormData({ ...formData, baseFee: parseFloat(e.target.value) || 0 });
                            setUseManualFees(false);
                          }}
                          className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={0}
                          step="any"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Annual Increase (%)</label>
                        <input
                          type="number"
                          value={formData.annualIncrease}
                          onChange={(e) => {
                            setFormData({ ...formData, annualIncrease: parseFloat(e.target.value) || 0 });
                            setUseManualFees(false);
                          }}
                          className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={0}
                          max={20}
                          step="any"
                        />
                      </div>
                    </div>

                    {/* Fee Schedule Preview / Edit */}
                    {feeSchedule.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-500">Year-by-Year Fees</span>
                          <span className="text-xs font-bold text-gray-700">
                            Total: {totalFees.toLocaleString('en-GB', { style: 'currency', currency: formData.currency })}
                          </span>
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {feeSchedule.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 w-14">{item.academicYear}</span>
                              <span className="text-gray-400 w-24">Sept {item.startYear}–Jul {item.endYear}</span>
                              <input
                                type="number"
                                value={item.annualFee}
                                onChange={(e) => handleFeeChange(idx, e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-blue-500"
                                min={0}
                                step="any"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      {isEditing ? 'Update' : 'Add Plan'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SchoolFeePlanModal;
